import { useMemo, useRef, useCallback, useState } from "react";

export interface UseFormChangesOptions<T> {
  compare?: (a: T, b: T) => boolean;
  /** Skip the comparison and always return `false`. Used in create mode. */
  skip?: boolean;
}

export interface UseFormChangesReturn<T> {
  hasChanges: boolean;
  reset: (next?: T) => void;
}

/**
 * Track whether a form's current state differs from its initial snapshot.
 *
 * - `initial` is captured once on mount; subsequent renders ignore changes
 *   to the prop so the user's edits aren't silently re-baselined.
 * - Default comparison is `JSON.stringify`. Pass `compare` for forms whose
 *   state contains `Date` / `Map` / non-stable property orderings.
 * - Call `reset(formData)` after a successful save to re-baseline so the
 *   button disables again until the next edit.
 */
export function useFormChanges<T>(
  initial: T,
  current: T,
  options: UseFormChangesOptions<T> = {},
): UseFormChangesReturn<T> {
  const initialRef = useRef<T>(initial);
  const { skip, compare } = options;
  // Bumped on reset() so the memo recomputes against the new baseline even
  // when `current` reference hasn't changed (e.g. save-success re-render).
  const [resetVersion, setResetVersion] = useState(0);

  const hasChanges = useMemo(() => {
    if (skip) return false;
    if (compare) return !compare(initialRef.current, current);
    return JSON.stringify(initialRef.current) !== JSON.stringify(current);
  }, [current, skip, compare, resetVersion]);

  const reset = useCallback(
    (next?: T) => {
      initialRef.current = (next ?? current) as T;
      setResetVersion((v) => v + 1);
    },
    [current],
  );

  return { hasChanges, reset };
}
